<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import Button from '@/components/Blocks/Buttons/Default.vue'
import { useMainStore } from '@/stores/main'
import QueryGuide from '@/components/Query/QueryGuide.vue'
import SyntaxHighlightedInput from '@/components/Query/SyntaxHighlightedInput.vue'
import TimeRangeDropdown from '@/components/EventSearch/TimeRangeDropdown.vue'
import { selectionToFilterParams } from '@/components/EventSearch/timeRangeUtils'

const router = useRouter()
const mainStore = useMainStore()
const query = ref('')
const widgetContainer = ref(null)
const timeRange = ref(null)

const examples = [
  { query: 'id.orig_h:54.73.176.175', label: 'Search by source IP' },
  { query: 'id.resp_p:4545 AND proto:tcp', label: 'Boolean operators' },
  { query: 'host:*.example.com', label: 'Wildcard queries' }
]

function submit() {
  const trimmedQuery = query.value.trim()

  if (!trimmedQuery) {
    return
  }

  const routeQuery = { query: trimmedQuery }
  const params = selectionToFilterParams(timeRange.value)
  if (params) {
    routeQuery.start = String(params.start)
    if (params.end) routeQuery.end = String(params.end)
    if (timeRange.value?.type === 'preset') {
      routeQuery.timePreset = timeRange.value.value
    }
  }

  router.push({ name: 'EventSearch', query: routeQuery })
}

function useExample(exampleQuery) {
  query.value = exampleQuery
}

function openFieldGuide() {
  mainStore.show_slideover(QueryGuide, {}, { width: 40 }, 'Query Guide')
}
</script>

<template>
  <div ref="widgetContainer" class="box dark p-4 bordered mb-5 d-flex column" style="height: 400px;">
    <button class="btn-help" @click="openFieldGuide" title="Query Field Reference">
      <font-awesome-icon :icon="['fas', 'question']" />
    </button>
    <div class="textarea-wrapper">
      <SyntaxHighlightedInput
        v-model="query"
        :multiline="true"
        placeholder="Enter Lucene query syntax to search logs..."
        @submit="submit"
        class="query-input"
      />
    </div>

    <div class="toolbar d-flex align-items-center">
      <div class="examples-inline">
        <span class="examples-label">Examples:</span>
        <span
          v-for="(example, index) in examples"
          :key="index"
          class="example-chip-inline"
          @click="useExample(example.query)"
        >
          <code>{{ example.query }}</code>
        </span>
      </div>
      <TimeRangeDropdown
        v-model="timeRange"
        class="ms-auto"
      />
      <Button class="ms-2" @click="submit" blue>search</Button>
    </div>
  </div>
</template>

<style scoped>
.textarea-wrapper {
  flex: 1;
  margin-bottom: 1rem;
  position: relative;
}

.query-input {
  height: 100%;
}

.toolbar {
  flex-shrink: 0;
}

.examples-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.examples-label {
  color: var(--text-faint);
  font-size: 0.75rem;
  font-weight: 500;
}

.example-chip-inline {
  cursor: pointer;
  transition: var(--transition);
}

.example-chip-inline code {
  color: var(--text-muted);
  font-size: 0.75rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  padding: 0.25rem 0.5rem;
  background: var(--surface-overlay-2);
  border-radius: var(--radius-sm);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: var(--transition);
}

.example-chip-inline:hover code {
  color: var(--accent-text);
  background: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.3);
}

.btn-help {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-faint);
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
}

.btn-help:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}

.btn-help:active {
  transform: scale(0.95);
}
</style>
